'use client';

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import Button from '@/components/atoms/button';
import Link from '@/components/atoms/link';
import Skeleton from '@/components/atoms/skeleton';
import InfoBanner from '@/components/molecules/info-banner';
import Gallery from '../gallery';
import AdditionalInfo from './components/additional-info';
import Header from './components/header';
import MainInfo from './components/main-info';
import { PdpDiscontinuedDetails, ProductDetailsProps } from './types';

// EWM-615 — the PDP content frame aligns with the PLP's standard content frame
// (EWM-563): `max-w-7xl` (1280px) + `px-4 lg:px-8` horizontal padding, so content
// width is consistent across PDP and PLP. Previously `max-w-6xl` (1152px, from
// Dan's early v0 PDP) left the PDP 128px narrower than the PLP. Re-used by every
// page-shell state so the chrome stays consistent regardless of which branch
// renders.
const SHELL_CONTAINER = 'mx-auto mb-16 max-w-7xl px-4 py-8 lg:px-8';

const SkeletonShell = ({ ariaLabel }: { ariaLabel: string }) => (
  <div className={SHELL_CONTAINER} role="status" aria-busy="true" aria-label={ariaLabel} data-pdp-state="loading">
    <Skeleton className="mb-6 h-4 w-1/3" />
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
      <Skeleton className="aspect-square w-full rounded-lg" />
      <div className="flex flex-col gap-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="mt-6 h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    </div>
  </div>
);

const NotFoundState = ({ backHref }: { backHref?: string }) => {
  const translate = useTranslations();
  return (
    <div className={`${SHELL_CONTAINER} text-center`} role="alert" data-pdp-state="not-found">
      <h1 className="text-22 font-bold text-gray-700">{translate('pdp.not-found-title')}</h1>
      <p className="text-14 mt-3 text-gray-600">{translate('pdp.not-found-description')}</p>
      <div className="mt-6">
        <Link
          href={backHref ?? '/'}
          className="text-primary inline-block font-semibold underline-offset-4 hover:underline"
        >
          {translate('pdp.not-found-back-link')}
        </Link>
      </div>
    </div>
  );
};

const ErrorState = ({ onRetry }: { onRetry?: () => void }) => {
  const translate = useTranslations();
  return (
    <div className={`${SHELL_CONTAINER} text-center`} role="alert" data-pdp-state="error">
      <h1 className="text-22 font-bold text-gray-700">{translate('pdp.error-title')}</h1>
      <p className="text-14 mt-3 text-gray-600">{translate('pdp.error-description')}</p>
      {onRetry && (
        <div className="mt-6 flex justify-center">
          <Button variant="primary" onClick={onRetry}>
            {translate('pdp.error-retry')}
          </Button>
        </div>
      )}
    </div>
  );
};

const DiscontinuedState = ({ details }: { details?: PdpDiscontinuedDetails }) => {
  const translate = useTranslations();
  return (
    <div className={SHELL_CONTAINER} role="alert" data-pdp-state="discontinued">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="mt-0.5 size-6 shrink-0 text-amber-500" aria-hidden />
          <div>
            <h1 className="text-primary text-18 font-bold">{translate('pdp.discontinued-title')}</h1>
            <p className="text-14 mt-1 text-gray-700">{translate('pdp.discontinued-description')}</p>
            {details?.supersededBy && (
              <p className="text-14 mt-3 text-gray-700">
                {translate('pdp.discontinued-superseded-by')}{' '}
                <Link href={details.supersededBy.url} className="text-primary font-semibold hover:underline">
                  {details.supersededBy.name}
                </Link>{' '}
                <span className="text-gray-500">({details.supersededBy.sku})</span>
              </p>
            )}
          </div>
        </div>
        <div className="mt-4">
          <Link href="/" className="text-primary text-14 font-semibold hover:underline">
            {translate('pdp.discontinued-back-link')}
          </Link>
        </div>
      </div>
    </div>
  );
};

// EWM-192 — page shell with role-aware action gating, 3-level breadcrumb,
// 2-column layout, below-fold sections, and a state machine for loading /
// error / not-found / discontinued. Stories EWM-193..EWM-198 fill the
// `<Gallery>`, `<MainInfo>`, and `<AdditionalInfo>` slots with their final
// designs; this story sets up the surrounding chrome.
const ProductDetails = ({
  state = 'ready',
  product,
  addToCart,
  getWishlists,
  canUsePurchaseLists,
  canAddToOwnWishlist,
  canAddToOthersWishlist,
  addToWishlists,
  removeFromWishlists,
  onChangeSKU,
  onSubmitQuote,
  defaultName,
  defaultEmail,
  defaultPhone,
  isGuest = false,
  isViewOnly = false,
  addToNewWishlist,
  addToCartDisabled = false,
  backOrdersAllowed = false,
  priceUnavailable = false,
  stockUnavailable = false,
  onRetry,
  notFoundBackHref,
  discontinuedDetails,
  description,
  specifications,
  downloads,
}: ProductDetailsProps) => {
  const translate = useTranslations();

  if (state === 'loading') return <SkeletonShell ariaLabel={translate('pdp.loading')} />;
  if (state === 'not-found') return <NotFoundState backHref={notFoundBackHref} />;
  if (state === 'error') return <ErrorState onRetry={onRetry} />;
  if (state === 'discontinued') return <DiscontinuedState details={discontinuedDetails} />;

  // state === 'ready' — full layout. Guards against the union gap so callers
  // who pass `state="ready"` but forget data props don't crash.
  if (!product) return null;

  return (
    <div data-pdp-state="ready">
      {/* EWM-618 — the top section sits on a full-width WHITE band so the grey
          pricing card + superseded callout inside it stand out. The storefront
          page is bg-gray-100 (layout shell), which previously left the top
          section grey — the grey cards then had no differentiation. */}
      <div className="bg-white">
        <div className={SHELL_CONTAINER}>
          {isViewOnly && (
            <InfoBanner className="mb-6">
              <b>{translate('common.view-only')}</b> {translate('cart.view-only-desc')}
            </InfoBanner>
          )}

          <Header className="mb-6 md:mb-8" product={product} />

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
            <Gallery images={product.carousel?.imageList ?? []} productName={product.name} badge={product.badge} />

            <MainInfo
              product={product}
              addToCartDisabled={addToCartDisabled}
              backOrdersAllowed={backOrdersAllowed}
              addToCart={addToCart!}
              getWishlists={getWishlists!}
              removeFromWishlists={removeFromWishlists!}
              addToWishlists={addToWishlists!}
              onChangeSKU={onChangeSKU ?? (() => {})}
              onSubmitQuote={onSubmitQuote ?? (() => Promise.resolve())}
              defaultName={defaultName}
              defaultEmail={defaultEmail}
              defaultPhone={defaultPhone}
              isGuest={isGuest}
              isViewOnly={isViewOnly}
              priceUnavailable={priceUnavailable}
              stockUnavailable={stockUnavailable}
              addToNewWishlist={addToNewWishlist!}
              canUsePurchaseLists={canUsePurchaseLists}
              canAddToOwnWishlist={canAddToOwnWishlist}
              canAddToOthersWishlist={canAddToOthersWishlist}
            />
          </div>
        </div>
      </div>

      {/* EWM-198 — full-width below-fold sections live outside the SHELL_CONTAINER
          so the alternating gray/white section backgrounds extend edge-to-edge. */}
      <AdditionalInfo
        description={description ?? product.description}
        specifications={specifications ?? product.specifications}
        downloads={downloads ?? product.downloads}
      />
    </div>
  );
};

export default ProductDetails;
